"use client"

import { useEffect } from "react"
import { useTour } from "@/hooks/use-tour"
import { TourCard } from "./tour-card"
import { IntroTourCard } from "./intro-tour-card"
import { TourTriggerButton } from "./tour-trigger-button"

interface TourOverlayProps {
  isMobile: boolean
  heroScreenRect?: DOMRect | null // Screen rect of the hero for highlighting
  onTourStateChange?: (isActive: boolean) => void
  onSelectHero?: () => void
  onOpenTowerMenu?: () => void
  onSelectTower?: () => void
  onOpenDroppodMenu?: () => void
  onCleanupStep?: () => void
}

export function TourOverlay({
  isMobile,
  heroScreenRect,
  onTourStateChange,
  onSelectHero,
  onOpenTowerMenu,
  onSelectTower,
  onOpenDroppodMenu,
  onCleanupStep,
}: TourOverlayProps) {
  const tour = useTour({
    isMobile,
    onSelectHero,
    onOpenTowerMenu,
    onSelectTower,
    onOpenDroppodMenu,
    onCleanupStep,
  })

  // Notify parent of tour state changes
  useEffect(() => {
    onTourStateChange?.(tour.tourActive || tour.showIntroCard)
  }, [tour.tourActive, tour.showIntroCard, onTourStateChange])

  // Determine the target rect for the current step
  // Some steps use DOM IDs, some use passed-in rects (like hero position)
  const getTargetRect = (): DOMRect | null | undefined => {
    if (!tour.currentStepData) return undefined

    // For hero-movement step, use the passed-in hero rect
    if (tour.currentStepData.id === "hero-movement" && heroScreenRect) {
      return heroScreenRect
    }

    // Otherwise let the TourCard find by ID
    return undefined
  }

  return (
    <div id="tour-overlay">
      {/* Tour trigger button */}
      {tour.showTourButton && (
        <TourTriggerButton
          onStartTour={tour.handleStartTour}
          onDismiss={tour.handleDismissTourButton}
          isMobile={isMobile}
        />
      )}

      {/* Intro card (mode selection) */}
      {tour.showIntroCard && (
        <IntroTourCard
          onStartTour={tour.handleStartTourFromIntro}
          onClose={tour.handleCloseIntroCard}
          isMobile={isMobile}
        />
      )}

      {/* Active tour step card */}
      {tour.tourActive && tour.currentStepData && (
        <TourCard
          title={tour.currentStepData.title}
          description={tour.getDescription(tour.currentStepData)}
          currentStep={tour.tourStep + 1}
          totalSteps={tour.maxTourSteps}
          onNext={tour.handleTourNext}
          onPrevious={tour.handleTourPrevious}
          onClose={tour.handleTourClose}
          targetIds={tour.currentStepData.targetIds}
          targetRect={getTargetRect()}
          isMobile={isMobile}
          cardPosition={tour.currentStepData.cardPosition}
        />
      )}
    </div>
  )
}
